import React from 'react';
import { BrowserRouter, Routes, Route, Navigate, useParams } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext.js';
import { Layout } from './components/Layout.js';
import { LoginPage } from './pages/LoginPage.js';
import { ServersListPage } from './pages/ServersListPage.js';
import { ServerOverviewPage } from './pages/ServerOverviewPage.js';
import { LiveConsolePage } from './pages/LiveConsolePage.js';
import { NetworkSettingsPage } from './pages/NetworkSettingsPage.js';
import { FileManagerPage } from './pages/FileManagerPage.js';
import { PlayersPage } from './pages/PlayersPage.js';
import { DatabaseManagerPage } from './pages/DatabaseManagerPage.js';
import { MetricsPage } from './pages/MetricsPage.js';
import { BackupsPage } from './pages/BackupsPage.js';
import { UsersPage } from './pages/UsersPage.js';
import { AuditLogPage } from './pages/AuditLogPage.js';

const RedirectToConfigFile: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  return <Navigate to={`/servers/${id}/files?open=server.toml`} replace />;
};

const RedirectToOverview: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  return <Navigate to={`/servers/${id}`} replace />;
};

const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, isLoading } = useAuth();

  if (isLoading) {
    return (
      <div className="h-screen w-screen flex items-center justify-center bg-panel-bg text-neutral-400 font-mono text-sm">
        Authenticating session...
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
};

export const App: React.FC = () => {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Routes>
          <Route path="/login" element={<LoginPage />} />

          <Route
            path="/"
            element={
              <ProtectedRoute>
                <Layout />
              </ProtectedRoute>
            }
          >
            <Route index element={<Navigate to="/servers" replace />} />
            <Route path="servers" element={<ServersListPage />} />
            <Route path="servers/:id" element={<ServerOverviewPage />} />
            <Route path="servers/:id/console" element={<LiveConsolePage />} />
            <Route path="servers/:id/network" element={<NetworkSettingsPage />} />
            <Route path="servers/:id/files" element={<FileManagerPage />} />
            <Route path="servers/:id/config" element={<RedirectToConfigFile />} />
            <Route path="servers/:id/gamemodes" element={<RedirectToOverview />} />
            <Route path="servers/:id/players" element={<PlayersPage />} />
            <Route path="servers/:id/database" element={<DatabaseManagerPage />} />
            <Route path="servers/:id/metrics" element={<MetricsPage />} />
            <Route path="servers/:id/backups" element={<BackupsPage />} />
            <Route path="users" element={<UsersPage />} />
            <Route path="audit" element={<AuditLogPage />} />
            <Route path="*" element={<Navigate to="/servers" replace />} />
          </Route>
        </Routes>
      </BrowserRouter>
    </AuthProvider>
  );
};
